/**
 * Service Financeiro (Fase 13.1 — ADR-0028).
 */

const PREFIXO_CONTA = "/api/contas-financeiras";
const PREFIXO_CATEGORIA = "/api/categorias-lancamento";
const PREFIXO_CENTRO = "/api/centros-custo";
const PREFIXO_LANC = "/api/lancamentos";

// ============================================================
// Contas
// ============================================================

export function listarContas(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.ativo !== undefined) usp.set("ativo", String(params.ativo));
  if (params.tipo) usp.set("tipo", params.tipo);
  const qs = usp.toString();
  return request(`${PREFIXO_CONTA}${qs ? `?${qs}` : ""}`);
}
export function criarConta(request, dados) {
  return request(PREFIXO_CONTA, { method: "POST", body: JSON.stringify(dados) });
}
export function atualizarConta(request, id, dados) {
  return request(`${PREFIXO_CONTA}/${id}`, { method: "PATCH", body: JSON.stringify(dados) });
}
export function desativarConta(request, id) {
  return request(`${PREFIXO_CONTA}/${id}`, { method: "DELETE" });
}

// ============================================================
// Categorias
// ============================================================

export function listarCategorias(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.ativo !== undefined) usp.set("ativo", String(params.ativo));
  if (params.tipo) usp.set("tipo", params.tipo);
  const qs = usp.toString();
  return request(`${PREFIXO_CATEGORIA}${qs ? `?${qs}` : ""}`);
}
export function criarCategoria(request, dados) {
  return request(PREFIXO_CATEGORIA, { method: "POST", body: JSON.stringify(dados) });
}
export function atualizarCategoria(request, id, dados) {
  return request(`${PREFIXO_CATEGORIA}/${id}`, { method: "PATCH", body: JSON.stringify(dados) });
}
export function desativarCategoria(request, id) {
  return request(`${PREFIXO_CATEGORIA}/${id}`, { method: "DELETE" });
}

// ============================================================
// Centros de Custo
// ============================================================

export function listarCentros(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.ativo !== undefined) usp.set("ativo", String(params.ativo));
  const qs = usp.toString();
  return request(`${PREFIXO_CENTRO}${qs ? `?${qs}` : ""}`);
}
export function criarCentro(request, dados) {
  return request(PREFIXO_CENTRO, { method: "POST", body: JSON.stringify(dados) });
}
export function atualizarCentro(request, id, dados) {
  return request(`${PREFIXO_CENTRO}/${id}`, { method: "PATCH", body: JSON.stringify(dados) });
}
export function desativarCentro(request, id) {
  return request(`${PREFIXO_CENTRO}/${id}`, { method: "DELETE" });
}

// ============================================================
// Condicoes de Pagamento (F-COMERCIAL.1)
// ============================================================

const PREFIXO_COND = "/api/condicoes-pagamento";

// Ancoras de parcela por evento do processo (Tarefa B2/F2) — espelha
// financeiro.models.ANCORAS_PARCELA do backend.
// "Montagem" = instalação na casa do cliente (OrdemInstalacao da logística):
// INICIO_INSTALACAO dispara quando a OI entra em execução; INSTALACAO quando
// a última OI conclui.
export const ANCORA_PARCELA_LABEL = {
  APROVACAO: "Aprovação (venda)",
  INICIO_PRODUCAO: "Início da produção",
  ENTREGA: "Entrega",
  INICIO_INSTALACAO: "Início da montagem",
  INSTALACAO: "Fim da montagem (instalação)",
};

export function fmtPercentualCurto(v) {
  const n = Number(v || 0);
  return Number.isInteger(n)
    ? String(n)
    : n.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
}

/**
 * Resumo humano das parcelas de uma condicao:
 * "70% aprovação + 15% início da produção + 15% entrega +7d".
 * Condicao sem parcelas por evento cai no modo legado (descricao ou Nx).
 */
export function resumirParcelasCondicao(condicao) {
  const parcelas = condicao?.parcelas || [];
  if (!parcelas.length) {
    return condicao?.descricao
      || `${condicao?.numero_parcelas || 1}x (modo legado por dias corridos)`;
  }
  return parcelas
    .slice()
    .sort((a, b) => a.ordem - b.ordem)
    .map((p) => {
      const ancora = (ANCORA_PARCELA_LABEL[p.ancora] || p.ancora).toLowerCase();
      const offset = Number(p.offset_dias || 0);
      const sufixo = offset ? ` ${offset > 0 ? "+" : ""}${offset}d` : "";
      return `${fmtPercentualCurto(p.percentual)}% ${ancora}${sufixo}`;
    })
    .join(" + ");
}

export function listarCondicoesPagamento(request, params = {}, options = {}) {
  const usp = new URLSearchParams();
  if (params.ativo !== undefined) usp.set("ativo", String(params.ativo));
  if (params.favorita !== undefined) usp.set("favorita", String(params.favorita));
  const qs = usp.toString();
  return request(`${PREFIXO_COND}${qs ? `?${qs}` : ""}`, options);
}
export function criarCondicaoPagamento(request, dados) {
  return request(PREFIXO_COND, { method: "POST", body: JSON.stringify(dados) });
}
export function atualizarCondicaoPagamento(request, id, dados) {
  return request(`${PREFIXO_COND}/${id}`, { method: "PATCH", body: JSON.stringify(dados) });
}
export function desativarCondicaoPagamento(request, id) {
  return request(`${PREFIXO_COND}/${id}`, { method: "DELETE" });
}

// ============================================================
// Lancamentos
// ============================================================

export function listarLancamentos(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.conta) usp.set("conta", params.conta);
  if (params.categoria) usp.set("categoria", params.categoria);
  if (params.centro_custo) usp.set("centro_custo", params.centro_custo);
  if (params.pedido) usp.set("pedido", params.pedido);
  if (params.tipo) usp.set("tipo", params.tipo);
  if (params.confirmado !== undefined) usp.set("confirmado", String(params.confirmado));
  if (params.conciliado !== undefined) usp.set("conciliado", String(params.conciliado));
  if (params.data_inicio) usp.set("data_inicio", params.data_inicio);
  if (params.data_fim) usp.set("data_fim", params.data_fim);
  const qs = usp.toString();
  return request(`${PREFIXO_LANC}${qs ? `?${qs}` : ""}`);
}
export function obterLancamento(request, id) {
  return request(`${PREFIXO_LANC}/${id}`);
}
export function criarLancamento(request, dados) {
  return request(PREFIXO_LANC, { method: "POST", body: JSON.stringify(dados) });
}
export function atualizarLancamento(request, id, dados) {
  return request(`${PREFIXO_LANC}/${id}`, { method: "PATCH", body: JSON.stringify(dados) });
}
export function confirmarLancamento(request, id, { data_caixa } = {}) {
  return request(`${PREFIXO_LANC}/${id}/confirmar`, {
    method: "POST",
    body: JSON.stringify({ data_caixa: data_caixa || null }),
  });
}
export function conciliarLancamento(request, id, { conciliado = true } = {}) {
  return request(`${PREFIXO_LANC}/${id}/conciliar`, {
    method: "POST",
    body: JSON.stringify({ conciliado }),
  });
}

// OFX import - multipart/form-data (sem JSON header)
export function importarOfx(request, { conta_id, arquivo, categoria_receita_default_id, categoria_despesa_default_id }) {
  const formData = new FormData();
  formData.append("conta_id", conta_id);
  formData.append("arquivo", arquivo);
  if (categoria_receita_default_id) formData.append("categoria_receita_default_id", categoria_receita_default_id);
  if (categoria_despesa_default_id) formData.append("categoria_despesa_default_id", categoria_despesa_default_id);
  return request(`${PREFIXO_LANC}/importar-ofx`, {
    method: "POST",
    body: formData,
    headers: {},  // sem Content-Type pra browser definir boundary
  });
}

// ============================================================
// Resumo (dashboard)
// ============================================================

// F13.2 - DRE + Fluxo de Caixa
export function obterDre(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.inicio) usp.set("inicio", params.inicio);
  if (params.fim) usp.set("fim", params.fim);
  if (params.unidade) usp.set("unidade", params.unidade);
  const qs = usp.toString();
  return request(`/api/financeiro/dre${qs ? `?${qs}` : ""}`);
}

export function obterFluxoCaixa(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.inicio) usp.set("inicio", params.inicio);
  if (params.fim) usp.set("fim", params.fim);
  if (params.granularidade) usp.set("granularidade", params.granularidade);
  if (params.unidade) usp.set("unidade", params.unidade);
  const qs = usp.toString();
  return request(`/api/financeiro/fluxo-caixa${qs ? `?${qs}` : ""}`);
}

// M7 (2026-05-26) — Dashboard executivo agregado
export function obterDashboardExecutivo(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.janela_dias) usp.set("janela_dias", String(params.janela_dias));
  if (params.unidade) usp.set("unidade", params.unidade);
  const qs = usp.toString();
  return request(`/api/financeiro/dashboard-executivo${qs ? `?${qs}` : ""}`);
}

export function obterResumoFinanceiro(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.unidade) usp.set("unidade", params.unidade);
  const qs = usp.toString();
  return request(`/api/financeiro/resumo${qs ? `?${qs}` : ""}`);
}

// F13.3 - Rentabilidade + Custos Indiretos (ADR-0030)

export function obterRentabilidadePeriodo(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.inicio) usp.set("inicio", params.inicio);
  if (params.fim) usp.set("fim", params.fim);
  if (params.unidade) usp.set("unidade", params.unidade);
  if (params.situacao) usp.set("situacao", params.situacao);
  const qs = usp.toString();
  return request(`/api/financeiro/rentabilidade${qs ? `?${qs}` : ""}`);
}

export function obterRentabilidadePedido(request, pedidoId) {
  return request(`/api/financeiro/rentabilidade-pedido/${pedidoId}`);
}

// F13.4 - Integracao bancaria (Open Banking)

const PREFIXO_CONEXAO = "/api/conexoes-bancarias";

export function listarConexoesBancarias(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.ativo !== undefined) usp.set("ativo", String(params.ativo));
  if (params.provider) usp.set("provider", params.provider);
  const qs = usp.toString();
  return request(`${PREFIXO_CONEXAO}${qs ? `?${qs}` : ""}`);
}

export function listarProvidersOpenBanking(request) {
  return request(`${PREFIXO_CONEXAO}/providers`);
}

export function criarConexaoBancaria(request, dados) {
  return request(PREFIXO_CONEXAO, { method: "POST", body: JSON.stringify(dados) });
}

export function desativarConexaoBancaria(request, id) {
  return request(`${PREFIXO_CONEXAO}/${id}`, { method: "DELETE" });
}

export function atualizarConexaoBancaria(request, id, dados) {
  return request(`${PREFIXO_CONEXAO}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(dados),
  });
}

export function sincronizarConexao(request, id, params = {}) {
  return request(`${PREFIXO_CONEXAO}/${id}/sincronizar`, {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export function obterHistoricoSincronizacao(request, id) {
  return request(`${PREFIXO_CONEXAO}/${id}/historico`);
}

// F13.4.1 - PluggyProvider real (ADR-0031 atualizado 2026-05-25)
// gerarConnectToken eh chamado antes de abrir o Pluggy Connect widget;
// retorna { access_token, expires_in } onde access_token vai para o widget
// como connect token (~30min validade).
export function gerarConnectTokenPluggy(request, { item_id, produtos } = {}) {
  return request("/api/pluggy/connect-token", {
    method: "POST",
    body: JSON.stringify({
      item_id: item_id || null,
      produtos: produtos || null,
    }),
  });
}

// M4 (2026-05-25) — Conciliacao bancaria visual
export function obterSugestoesConciliacao(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.conta_id) usp.set("conta_id", params.conta_id);
  if (params.inicio) usp.set("inicio", params.inicio);
  if (params.fim) usp.set("fim", params.fim);
  if (params.janela_dias !== undefined) usp.set("janela_dias", String(params.janela_dias));
  return request(`/api/financeiro/conciliacao/sugestoes?${usp.toString()}`);
}
export function conciliarPar(request, manual_id, ofx_id) {
  return request("/api/financeiro/conciliacao/conciliar-par", {
    method: "POST",
    body: JSON.stringify({ manual_id, ofx_id }),
  });
}

// M2.B (2026-05-25) — Comissoes financeiras
export function obterComissoesPorParceiro(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.inicio) usp.set("inicio", params.inicio);
  if (params.fim) usp.set("fim", params.fim);
  if (params.unidade) usp.set("unidade", params.unidade);
  const qs = usp.toString();
  return request(`/api/financeiro/comissoes${qs ? `?${qs}` : ""}`);
}
export function gerarComissoesRetroativo(request, params = {}) {
  return request("/api/financeiro/comissoes/gerar-retroativo", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export function obterCustosIndiretos(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.inicio) usp.set("inicio", params.inicio);
  if (params.fim) usp.set("fim", params.fim);
  if (params.unidade) usp.set("unidade", params.unidade);
  if (params.custo_hora !== undefined && params.custo_hora !== "")
    usp.set("custo_hora", params.custo_hora);
  if (params.multa_diaria !== undefined && params.multa_diaria !== "")
    usp.set("multa_diaria", params.multa_diaria);
  const qs = usp.toString();
  return request(`/api/financeiro/custos-indiretos${qs ? `?${qs}` : ""}`);
}

// ============================================================
// Labels e helpers UI
// ============================================================

export const TIPOS_CONTA = [
  { value: "CORRENTE", label: "Conta corrente" },
  { value: "POUPANCA", label: "Poupanca" },
  { value: "CAIXA", label: "Caixa interno" },
  { value: "CARTAO", label: "Cartao de crédito" },
  { value: "OUTROS", label: "Outros" },
];

export const TIPOS_CATEGORIA = [
  { value: "RECEITA", label: "Receita", tone: "success" },
  { value: "DESPESA", label: "Despesa", tone: "danger" },
];

export const TIPOS_LANCAMENTO = [
  { value: "ENTRADA", label: "Entrada", tone: "success" },
  { value: "SAIDA", label: "Saída", tone: "danger" },
];

export const TIPO_CONTA_LABELS = Object.fromEntries(TIPOS_CONTA.map((t) => [t.value, t.label]));
export const TIPO_CATEGORIA_LABELS = Object.fromEntries(TIPOS_CATEGORIA.map((t) => [t.value, t.label]));

// M2.A.3 (2026-05-25): classificacoes DRE oficiais (espelha backend
// `financeiro/models.py` CLASSIFICACOES_DRE). `tipo_aplicavel` define em
// quais tipos de categoria a classificacao eh valida (RECEITA / DESPESA /
// AMBOS). UI usa pra filtrar dropdowns conforme tipo selecionado.
export const CLASSIFICACOES_DRE = [
  { value: "RECEITA_OPERACIONAL", label: "Receitas Operacionais", tipo_aplicavel: "RECEITA" },
  { value: "DEDUCAO_RECEITA_BRUTA", label: "Deducoes de Receita Bruta", tipo_aplicavel: "DESPESA" },
  { value: "CUSTO_OPERACIONAL", label: "Custos Operacionais", tipo_aplicavel: "DESPESA" },
  { value: "DESPESA_OPERACIONAL", label: "Despesas Operacionais", tipo_aplicavel: "DESPESA" },
  { value: "RECEITA_FINANCEIRA", label: "Receitas Financeiras", tipo_aplicavel: "RECEITA" },
  { value: "DESPESA_FINANCEIRA", label: "Despesas Financeiras", tipo_aplicavel: "DESPESA" },
  { value: "RECEITA_NAO_OPERACIONAL", label: "Receitas Não Operacionais", tipo_aplicavel: "RECEITA" },
  { value: "DESPESA_NAO_OPERACIONAL", label: "Despesas Não Operacionais", tipo_aplicavel: "DESPESA" },
  { value: "INVESTIMENTO_EMPRESTIMO", label: "Investimentos e Emprestimos", tipo_aplicavel: "DESPESA" },
  { value: "OUTROS", label: "Outros (não classificado)", tipo_aplicavel: "AMBOS" },
];

export const CLASSIFICACAO_DRE_LABELS = Object.fromEntries(
  CLASSIFICACOES_DRE.map((c) => [c.value, c.label]),
);

export const TIPO_LANC_LABELS = Object.fromEntries(TIPOS_LANCAMENTO.map((t) => [t.value, t.label]));
export const TIPO_LANC_TONES = Object.fromEntries(TIPOS_LANCAMENTO.map((t) => [t.value, t.tone]));

export function formatarReais(valor) {
  const num = typeof valor === "string" ? Number(valor) : valor;
  if (typeof num !== "number" || Number.isNaN(num)) return "R$ 0,00";
  return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}
